import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Autocomplete, TextField, Snackbar, Alert, CircularProgress,
    Dialog,
} from '@mui/material';
import PhoneInput from '../../components/common/PhoneInput';
import AdditionalCustomersSection from './AdditionalCustomersSection';
import { useBooking } from '../../contexts/BookingContext';
import { useBookingDetails } from '../../contexts/BookingDetailsContext';
import { useModal } from '../../contexts/ModalContext';
import { fetchBookingLeadSelect } from '../../services/common';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
    bg: '#f7fbf8',
    white: '#ffffff',
    ink: '#0f1f14',
    subtle: '#6b7f74',
    muted: '#a0b0a8',
    border: '#dde8e2',
    border2: '#c0d4c8',
    surface: '#f0f6f2',
    mint: '#0f7a4a',
    mint2: '#16a163',
    dark: '#0f1f14',
    darkSubtle: '#3a5a44',
    warn: '#fffbf0',
    warnBorder: '#edd890',
    warnText: '#8a6e1a',
};

// ─── Primitives ───────────────────────────────────────────────────────────────
function FieldLabel({ children, required }) {
    return (
        <label style={{
            fontSize: 10.5, fontWeight: 700, color: T.subtle,
            textTransform: 'uppercase', letterSpacing: '0.5px',
            display: 'block', marginBottom: 5,
        }}>
            {children}
            {required && <span style={{ color: T.mint, marginLeft: 2 }}>*</span>}
        </label>
    );
}

function Field({ label, required, children, style }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, ...style }}>
            {label && <FieldLabel required={required}>{label}</FieldLabel>}
            {children}
        </div>
    );
}

function Card({ children, style }) {
    return (
        <div style={{
            background: T.white, borderRadius: 14,
            padding: '18px 20px', border: `1.5px solid ${T.border}`,
            display: 'flex', flexDirection: 'column', gap: 14, ...style,
        }}>
            {children}
        </div>
    );
}

function SectionHead({ icon, label, step }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
            <div style={{
                width: 26, height: 26, borderRadius: '50%', background: T.mint,
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800, flexShrink: 0,
            }}>{step}</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{icon} {label}</span>
        </div>
    );
}

function CheckToggle({ checked, onChange, label, disabled }) {
    return (
        <div
            onClick={disabled ? undefined : () => onChange(!checked)}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 10,
                cursor: disabled ? 'not-allowed' : 'pointer',
                background: checked ? '#f0faf5' : T.surface,
                border: `1.5px solid ${checked ? '#b6e4ce' : T.border}`,
                opacity: disabled ? 0.5 : 1, transition: 'all 150ms',
            }}
        >
            <div style={{
                width: 17, height: 17, borderRadius: 5, flexShrink: 0,
                border: `2px solid ${checked ? T.mint : T.border2}`,
                background: checked ? T.mint : T.white,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 150ms',
            }}>
                {checked && <span style={{ color: '#fff', fontSize: 10, fontWeight: 900 }}>✓</span>}
            </div>
            <span style={{ fontSize: 13, color: T.ink, fontWeight: checked ? 600 : 400 }}>{label}</span>
        </div>
    );
}

// Shared MUI sx for consistent input styling
const autoSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px', fontSize: 13,
        '& fieldset': { borderColor: T.border, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: T.border2 },
        '&.Mui-focused fieldset': { borderColor: T.mint, borderWidth: '1.5px' },
        '&.Mui-disabled': { backgroundColor: T.surface },
    },
    '& .MuiInputLabel-root': { fontSize: 13, color: T.subtle },
    '& .MuiInputBase-input': { fontSize: 13 },
    '& .MuiFormHelperText-root': { fontSize: 10.5 },
};

const STEPS = ['Lead & Customer', 'Project & Unit', 'Notes'];

function StepBar({ current, onGo }) {
    return (
        <div style={{ display: 'flex', padding: '0 24px 16px' }}>
            {STEPS.map((label, i) => {
                const s = i + 1;
                const done = s < current;
                const active = s === current;
                return (
                    <div key={s} style={{ flex: 1, cursor: done ? 'pointer' : 'default' }}
                        onClick={() => done && onGo(s)}>
                        <div style={{
                            height: 3, borderRadius: 99, marginBottom: 7,
                            background: done || active ? T.mint2 : 'rgba(255,255,255,0.10)',
                            transition: 'background 300ms',
                        }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{
                                width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
                                background: done ? T.mint2 : active ? 'rgba(22,161,99,0.2)' : 'rgba(255,255,255,0.06)',
                                border: active ? `2px solid ${T.mint2}` : done ? 'none' : '2px solid rgba(255,255,255,0.10)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 8, color: done ? '#fff' : active ? T.mint2 : 'rgba(255,255,255,0.3)',
                                fontWeight: 900,
                            }}>
                                {done ? '✓' : s}
                            </div>
                            <span style={{
                                fontSize: 10, fontWeight: active ? 700 : 400,
                                color: active ? '#7ddfac' : done ? '#4a9e6a' : 'rgba(255,255,255,0.28)',
                            }}>{label}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
const CreateNewBooking = ({ initialData, open, onClose, leads, units, filtered_projects }) => {
    const { addNewBooking } = useBooking();
    const bookingDetailsContext = useBookingDetails();
    const { openSuccessModal, openErrorModal } = useModal();

    const isEditMode = Boolean(initialData?.id);
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState(() => {
        if (initialData) {
            return {
                id: initialData.id,
                lead: initialData.lead?.value || initialData.lead?.id || initialData.lead || null,
                customer_name: initialData.customer?.customer_name || initialData.customer_name || '',
                customer_email: initialData.customer?.customer_email || initialData.customer_email || '',
                customer_phone: initialData.customer?.customer_phone || initialData.customer_phone || '',
                project: initialData.project?.value || initialData.project?.id || initialData.project || null,
                unit: initialData.unit?.value || initialData.unit?.id || initialData.unit || null,
                notes: initialData.notes || '',
                additional_customers: initialData.additional_customers || [],
            };
        }
        return {
            lead: null, customer_name: '', customer_email: '', customer_phone: '',
            project: null, unit: null, notes: '', additional_customers: [],
        };
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [sameAsLead, setSameAsLead] = useState(false);

    // Lead search state
    const [leadSearchInput, setLeadSearchInput] = useState('');
    const [filteredLeads, setFilteredLeads] = useState(leads || []);
    const [isSearchingLeads, setIsSearchingLeads] = useState(false);
    const searchTimeoutRef = useRef(null);

    // ── All original effects preserved ───────────────────────────────────────
    useEffect(() => {
        if (leads?.length > 0 && (!leadSearchInput || leadSearchInput.trim() === '')) {
            setFilteredLeads(leads.slice(0, 50));
        } else if (!leads?.length) {
            setFilteredLeads([]);
        }
    }, [leads]);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (!leadSearchInput || leadSearchInput.trim() === '') {
            setFilteredLeads(leads?.slice(0, 50) || []);
            setIsSearchingLeads(false);
            return;
        }
        setIsSearchingLeads(true);
        searchTimeoutRef.current = setTimeout(async () => {
            const term = leadSearchInput.trim();
            if (term.length >= 2) {
                try {
                    const apiResults = await fetchBookingLeadSelect(term, 100);
                    if (apiResults?.length > 0) {
                        setFilteredLeads(apiResults.map(l => ({
                            value: l.value || l.id,
                            label: l.label || `${l.first_name || ''} ${l.last_name || ''}`.trim(),
                            lead_id: l.lead_id, email: l.email, mobile: l.mobile, agent_id: l.agent_id,
                        })));
                        setIsSearchingLeads(false);
                        return;
                    }
                } catch {}
            }
            const lower = term.toLowerCase();
            setFilteredLeads(
                (leads || []).filter(l => {
                    const lbl = (l?.label || l?.name || '').toLowerCase();
                    const id = (l?.lead_id || l?.id || '').toString().toLowerCase();
                    return lbl.includes(lower) || id.includes(lower);
                }).slice(0, 100)
            );
            setIsSearchingLeads(false);
        }, 300);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [leadSearchInput, leads]);

    const filteredUnits = useMemo(() => {
        if (!formData.project) return [];
        const pid = formData.project?.value || formData.project?.id || formData.project;
        return units[pid] || [];
    }, [formData.project, units]);

    useEffect(() => {
        if (formData.lead && !isEditMode) {
            setFormData(f => ({ ...f, customer_name: '', customer_email: '', customer_phone: '' }));
            setSameAsLead(false);
        }
    }, [formData.lead, isEditMode]);

    useEffect(() => {
        if (formData.lead && sameAsLead) {
            setFormData(f => ({
                ...f,
                customer_name: formData.lead.label || '',
                customer_email: formData.lead.email || '',
                customer_phone: formData.lead.mobile || '',
            }));
        }
    }, [formData.lead, sameAsLead]);

    useEffect(() => {
        if (formData.unit) setFormData(f => ({ ...f, base_price: formData.unit.base_price || '' }));
    }, [formData.unit]);

    useEffect(() => {
        if (!open) {
            setLeadSearchInput(''); setFilteredLeads(leads?.slice(0, 50) || []);
            setIsSearchingLeads(false); setStep(1);
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        }
    }, [open, leads]);

    useEffect(() => {
        if (initialData && open) {
            const mk = (data, type) => {
                if (!data) return null;
                if (type === 'lead') return { value: data.id, label: data.name, id: data.id, lead_id: data.id, email: data.email, mobile: data.phone };
                if (type === 'project') return { value: data.id, label: data.name, id: data.id };
                if (type === 'unit') return { value: data.id, label: data.name, id: data.id, base_price: data.base_price };
                return data;
            };
            const leadOpt = mk(initialData.lead, 'lead');
            const cName = initialData.customer?.customer_name || initialData.customer_name || '';
            const cEmail = initialData.customer?.customer_email || initialData.customer_email || '';
            const cPhone = initialData.customer?.customer_phone || initialData.customer_phone || '';
            setSameAsLead(leadOpt && cName === leadOpt.label && cEmail === leadOpt.email && cPhone === leadOpt.mobile);
            setFormData({
                id: initialData.id, lead: leadOpt, customer_name: cName,
                customer_email: cEmail, customer_phone: cPhone,
                project: mk(initialData.project, 'project'),
                unit: mk(initialData.unit, 'unit'),
                notes: initialData.notes || '',
                additional_customers: initialData.additional_customers || [],
            });
            setErrors({});
        }
    }, [initialData, open]);

    useEffect(() => {
        if (initialData && open) {
            setFormData(prev => ({ ...prev, additional_customers: initialData.additional_customers || [] }));
        }
    }, [initialData, open]);

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = (data) => {
        const errs = {};
        if (!data.lead) errs.lead = 'Required';
        if (sameAsLead) {
            if (!data.lead?.label) errs.customer_name = 'Lead name is required';
            if (!data.lead?.email && !data.lead?.mobile) {
                errs.customer_email = 'Either email or mobile is required';
                errs.customer_phone = 'Either email or mobile is required';
            }
        } else {
            if (!data.customer_name) errs.customer_name = 'Required';
            if (!data.customer_email?.trim() && !data.customer_phone?.trim()) {
                errs.customer_email = 'Either email or mobile is required';
                errs.customer_phone = 'Either email or mobile is required';
            }
        }
        if (!data.project) errs.project = 'Required';
        const isDraft = (data.status || 'draft').toLowerCase() === 'draft';
        if (!isDraft && !data.unit) errs.unit = 'Unit is required for non-draft bookings';
        data.additional_customers?.forEach((c, i) => {
            if (!c.name?.trim()) errs[`additional_customer_${i}_name`] = 'Name is required';
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
    };

    const handleLeadChange = (_, value) => {
        setFormData(f => ({ ...f, lead: value, ...(isEditMode ? {} : { customer_name: '', customer_email: '', customer_phone: '' }) }));
        if (!isEditMode) setSameAsLead(false);
        if (errors.lead) setErrors(p => ({ ...p, lead: '' }));
    };

    const handleProjectChange = (_, value) => {
        setFormData(f => ({ ...f, project: value, ...(isEditMode ? {} : { unit: null }) }));
        if (errors.project) setErrors(p => ({ ...p, project: '' }));
    };

    const handleUnitChange = (_, value) => {
        setFormData(f => ({ ...f, unit: value }));
        if (errors.unit) setErrors(p => ({ ...p, unit: '' }));
    };

    const handleSameAsLeadChange = (checked) => {
        setSameAsLead(checked);
        if (checked && formData.lead) {
            setFormData(f => ({ ...f, customer_name: formData.lead.label || '', customer_email: formData.lead.email || '', customer_phone: formData.lead.mobile || '' }));
        } else if (!checked) {
            setFormData(f => ({ ...f, customer_name: '', customer_email: '', customer_phone: '' }));
        }
    };

    const handleAdditionalCustomerChange = useCallback((customers) => {
        setFormData(f => ({ ...f, additional_customers: customers }));
    }, []);

    const handleSubmit = async () => {
        let data = { ...formData };
        if (sameAsLead && formData.lead) {
            data = { ...data, customer_name: formData.lead.label || '', customer_email: formData.lead.email || '', customer_phone: formData.lead.mobile || '' };
        }
        if (!validate(data)) return;
        setIsSubmitting(true);
        try {
            const payload = {
                ...data,
                customer_phone: data.customer_phone?.trim(),
                lead: data.lead?.value || data.lead || null,
                project: data.project?.value || data.project?.id || data.project || null,
                unit: data.unit?.value || data.unit?.id || data.unit || null,
                additional_customers: data.additional_customers?.map(c => ({ name: c.name || '', email: c.email || '', phone: c.phone || '' })) || [],
            };
            ['lead', 'project', 'unit'].forEach(k => {
                if (payload[k] && typeof payload[k] === 'object') payload[k] = payload[k].value || payload[k].id || null;
            });
            onClose();
            if (isEditMode) {
                const res = bookingDetailsContext?.updateBooking
                    ? await bookingDetailsContext.updateBooking(payload.id, payload)
                    : await addNewBooking(payload);
                openSuccessModal('Success', `Booking ${(res.data || res).booking_id} updated successfully!`);
            } else {
                if (!bookingDetailsContext) {
                    const res = await addNewBooking(payload);
                    openSuccessModal('Success', `Booking ${(res.data || res).booking_id} created successfully!`);
                } else {
                    openErrorModal('Invalid Operation', 'Creating new bookings is only available from the bookings list.');
                }
            }
        } catch (err) {
            openErrorModal('Error creating booking', `Error creating booking ${err.booking_id}: ${err.detail}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const canProceed = (s) => {
        if (s === 1) return !!formData.lead && (sameAsLead || !!formData.customer_name);
        if (s === 2) return !!formData.project;
        return true;
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Dialog
            open={open} onClose={onClose} maxWidth={false}
            PaperProps={{
                style: {
                    background: 'transparent', boxShadow: 'none',
                    margin: 16, overflow: 'visible',
                }
            }}
        >
            <div style={{
                background: T.bg, borderRadius: 20,
                width: '100%', minWidth: 'min(600px, 90vw)', maxWidth: 640,
                maxHeight: '92vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 32px 96px rgba(0,0,0,0.25), 0 0 0 1px rgba(15,122,74,0.08)',
                overflow: 'hidden',
                fontFamily: "'DM Sans', 'Plus Jakarta Sans', sans-serif",
            }}>

                {/* ── Header ── */}
                <div style={{ background: T.dark, flexShrink: 0 }}>
                    <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                            <div style={{
                                width: 42, height: 42, borderRadius: 11,
                                background: 'linear-gradient(135deg, #0f7a4a, #16a163)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19,
                            }}>📋</div>
                            <div>
                                <div style={{ fontSize: 15.5, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                                    {isEditMode ? 'Edit Booking' : 'New Draft Booking'}
                                </div>
                                <div style={{ fontSize: 11.5, color: '#5a9e7a', marginTop: 1 }}>
                                    {isEditMode ? 'Update the booking details below' : 'Link a lead · assign a unit · save as draft'}
                                </div>
                            </div>
                        </div>
                        <button onClick={onClose} style={{
                            background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8,
                            color: '#7ab090', cursor: 'pointer', width: 30, height: 30,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 18, fontWeight: 700,
                        }}>×</button>
                    </div>
                    <StepBar current={step} onGo={setStep} />
                </div>

                {/* ── Body ── */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* STEP 1 — Lead & Customer */}
                    {step === 1 && (<>
                        <Card>
                            <SectionHead icon="👤" label="Select Lead" step="1" />
                            <Field label="Lead" required>
                                <Autocomplete
                                    options={filteredLeads}
                                    getOptionKey={o => o?.value || o?.id || o?.lead_id || o?.label || Math.random()}
                                    getOptionLabel={o => !o ? '' : o?.lead_id ? `${o.label || ''} (${o.lead_id})` : (o?.label || '')}
                                    value={formData.lead}
                                    onChange={handleLeadChange}
                                    onInputChange={(_, v, reason) => {
                                        if (reason === 'input') setLeadSearchInput(v);
                                        else if (reason === 'clear') setLeadSearchInput('');
                                    }}
                                    disabled={isEditMode && !!(initialData?.lead?.id ?? initialData?.lead?.value ?? initialData?.lead)}
                                    loading={isSearchingLeads}
                                    filterOptions={o => o}
                                    renderOption={(props, o) => (
                                        <li {...props} key={o?.value || o?.id || o?.label || Math.random()}
                                            style={{ ...props.style, fontFamily: 'inherit', fontSize: 13 }}>
                                            <div>
                                                <span style={{ fontWeight: 600 }}>{o?.label || ''}</span>
                                                {o?.lead_id && <span style={{ color: T.muted, fontSize: 11, marginLeft: 6 }}>#{o.lead_id}</span>}
                                                {o?.mobile && <div style={{ fontSize: 11, color: T.subtle }}>{o.mobile}</div>}
                                            </div>
                                        </li>
                                    )}
                                    renderInput={params => (
                                        <TextField
                                            {...params} placeholder="Search leads…"
                                            error={!!errors.lead} helperText={errors.lead}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (<>{isSearchingLeads ? <CircularProgress size={16} sx={{ color: T.mint }} /> : null}{params.InputProps.endAdornment}</>),
                                            }}
                                            sx={autoSx}
                                        />
                                    )}
                                />
                            </Field>

                            {/* Lead preview chip */}
                            {formData.lead && (
                                <div style={{
                                    padding: '10px 12px', borderRadius: 10,
                                    background: '#f0faf5', border: '1px solid #b6e4ce',
                                    display: 'flex', alignItems: 'center', gap: 10,
                                }}>
                                    <div style={{
                                        width: 34, height: 34, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #0f7a4a, #16a163)',
                                        color: '#fff', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
                                    }}>
                                        {(formData.lead.label || '').split(' ').map(w => w[0]).slice(0, 2).join('')}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{formData.lead.label}</div>
                                        <div style={{ fontSize: 11, color: '#4a8c66' }}>
                                            {[formData.lead.email, formData.lead.mobile].filter(Boolean).join(' · ')}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card>
                            <SectionHead icon="🪪" label="Customer Details" step="2" />
                            <CheckToggle
                                checked={sameAsLead}
                                onChange={handleSameAsLeadChange}
                                label="Customer same as Lead"
                                disabled={!formData.lead || isEditMode}
                            />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                <Field label="Customer Name" required style={{ flex: '1 1 100%' }}>
                                    <TextField
                                        name="customer_name" value={formData.customer_name}
                                        onChange={handleChange} placeholder="Full name"
                                        disabled={sameAsLead && !isEditMode}
                                        error={!!errors.customer_name} helperText={errors.customer_name}
                                        size="small" fullWidth sx={autoSx}
                                    />
                                </Field>
                                <Field label="Email" style={{ flex: '1 1 calc(50% - 5px)' }}>
                                    <TextField
                                        name="customer_email" value={formData.customer_email}
                                        onChange={handleChange} type="email" placeholder="email@example.com"
                                        disabled={sameAsLead && !isEditMode}
                                        error={!!errors.customer_email} helperText={errors.customer_email}
                                        size="small" fullWidth sx={autoSx}
                                    />
                                </Field>
                                <Field label="Phone" style={{ flex: '1 1 calc(50% - 5px)' }}>
                                    <PhoneInput
                                        label=""
                                        value={formData.customer_phone}
                                        onChange={v => setFormData(f => ({ ...f, customer_phone: v }))}
                                        disabled={sameAsLead && !isEditMode}
                                        sx={autoSx}
                                    />
                                    {errors.customer_phone && (
                                        <span style={{ fontSize: 10.5, color: '#c0392b', marginTop: 3 }}>{errors.customer_phone}</span>
                                    )}
                                </Field>
                            </div>
                        </Card>

                        <Card>
                            <SectionHead icon="👥" label="Co-Applicants" step="3" />
                            <AdditionalCustomersSection
                                customers={formData.additional_customers || []}
                                onChange={handleAdditionalCustomerChange}
                                errors={errors}
                                disabled={isSubmitting}
                            />
                        </Card>
                    </>)}

                    {/* STEP 2 — Project & Unit */}
                    {step === 2 && (
                        <Card>
                            <SectionHead icon="🏢" label="Project & Unit" step="4" />

                            <Field label="Project" required>
                                <Autocomplete
                                    options={filtered_projects}
                                    getOptionKey={o => o?.value || o?.id || o?.label || Math.random()}
                                    getOptionLabel={o => o?.label || ''}
                                    value={formData.project}
                                    onChange={handleProjectChange}
                                    renderInput={params => (
                                        <TextField
                                            {...params} placeholder="Select project…"
                                            error={!!errors.project} helperText={errors.project}
                                            sx={autoSx}
                                        />
                                    )}
                                />
                            </Field>

                            <Field label={`Unit${(formData.status || 'draft').toLowerCase() === 'draft' ? ' (optional for draft)' : ''}`}>
                                <Autocomplete
                                    options={filteredUnits}
                                    getOptionKey={o => o?.value || o?.id || o?.label || Math.random()}
                                    getOptionLabel={o => o?.label || ''}
                                    value={formData.unit}
                                    onChange={handleUnitChange}
                                    disabled={!formData.project}
                                    noOptionsText={filteredUnits.length === 0 && formData.project ? 'No units available for this project' : 'No options'}
                                    renderOption={(props, o) => (
                                        <li {...props} key={o?.value || o?.id || o?.label || Math.random()}
                                            style={{ ...props.style, fontSize: 13, fontFamily: 'inherit' }}>
                                            {o?.label || 'Unknown'}
                                        </li>
                                    )}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            placeholder={formData.project ? 'Select unit…' : 'Select a project first'}
                                            error={!!errors.unit}
                                            helperText={
                                                errors.unit ||
                                                (!formData.project ? 'Select a project first' :
                                                    filteredUnits.length === 0 ? 'No units available' :
                                                        `${filteredUnits.length} unit${filteredUnits.length !== 1 ? 's' : ''} available`)
                                            }
                                            sx={autoSx}
                                        />
                                    )}
                                />
                            </Field>

                            {formData.project && !formData.unit && (
                                <div style={{
                                    padding: '9px 12px', borderRadius: 10,
                                    background: T.warn, border: `1px solid ${T.warnBorder}`,
                                    fontSize: 12, color: T.warnText,
                                    display: 'flex', alignItems: 'center', gap: 8,
                                }}>
                                    <span>💡</span> Unit is optional for draft bookings — you can assign it later.
                                </div>
                            )}

                            {formData.unit && (
                                <div style={{
                                    padding: '14px 16px', borderRadius: 12,
                                    background: 'linear-gradient(135deg, #f0faf5, #e8f5ee)',
                                    border: '1.5px solid #a8ddc0',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{formData.unit.label}</div>
                                        <div style={{ fontSize: 11.5, color: '#4a8c66', marginTop: 2 }}>{formData.project?.label}</div>
                                    </div>
                                    <div style={{
                                        fontSize: 10, fontWeight: 700, color: T.mint,
                                        background: '#d4f5e4', borderRadius: 99, padding: '3px 10px',
                                    }}>SELECTED</div>
                                </div>
                            )}
                        </Card>
                    )}

                    {/* STEP 3 — Notes & Review */}
                    {step === 3 && (<>
                        <Card>
                            <div style={{ fontSize: 11, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                                Booking Summary
                            </div>
                            {[
                                { icon: '👤', label: 'Lead', value: formData.lead?.label || '—' },
                                { icon: '🪪', label: 'Customer', value: formData.customer_name || (sameAsLead ? formData.lead?.label : null) || '—' },
                                { icon: '🏢', label: 'Project', value: formData.project?.label || '—' },
                                { icon: '🏠', label: 'Unit', value: formData.unit?.label || 'To be assigned' },
                                ...(formData.additional_customers?.filter(c => c.name).length > 0 ? [{
                                    icon: '👥', label: 'Co-applicants',
                                    value: formData.additional_customers.filter(c => c.name).map(c => c.name).join(', '),
                                }] : []),
                            ].map(({ icon, label, value }) => (
                                <div key={label} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                                    padding: '8px 0', borderBottom: `1px solid ${T.border}`, gap: 12,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                        <span style={{ fontSize: 13 }}>{icon}</span>
                                        <span style={{ fontSize: 12, color: T.subtle }}>{label}</span>
                                    </div>
                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, textAlign: 'right' }}>{value}</span>
                                </div>
                            ))}
                        </Card>

                        <Card>
                            <SectionHead icon="📝" label="Notes" step="5" />
                            <TextField
                                name="notes" value={formData.notes} onChange={handleChange}
                                multiline rows={4} fullWidth
                                placeholder="Special requirements, payment preferences, or any remarks…"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px', fontSize: 13,
                                        '& fieldset': { borderColor: T.border, borderWidth: '1.5px' },
                                        '&:hover fieldset': { borderColor: T.border2 },
                                        '&.Mui-focused fieldset': { borderColor: T.mint, borderWidth: '1.5px' },
                                    },
                                    '& .MuiInputBase-input': { fontSize: 13, lineHeight: 1.6 },
                                }}
                            />
                            <span style={{ fontSize: 11, color: T.muted }}>Internal notes — not visible to the customer.</span>
                        </Card>

                        <div style={{
                            padding: '10px 14px', borderRadius: 10, fontSize: 12,
                            color: '#4a8c66', background: '#f0faf5', border: '1px solid #b6e4ce',
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <span>💾</span>
                            {isEditMode
                                ? 'Review changes above before updating.'
                                : 'This booking will be saved as a Draft. You can assign a unit and update status later.'
                            }
                        </div>
                    </>)}
                </div>

                {/* ── Footer ── */}
                <div style={{
                    padding: '13px 24px', background: T.white,
                    borderTop: `1.5px solid ${T.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexShrink: 0, gap: 10,
                }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {step > 1 && (
                            <button onClick={() => setStep(s => s - 1)} style={{
                                background: 'transparent', border: `1.5px solid ${T.border}`,
                                borderRadius: 10, padding: '8px 14px', fontSize: 12.5,
                                fontWeight: 600, color: T.darkSubtle, cursor: 'pointer', fontFamily: 'inherit',
                            }}>← Back</button>
                        )}
                        <button onClick={onClose} style={{
                            background: 'transparent', border: 'none', padding: '8px 4px',
                            fontSize: 12.5, color: T.muted, cursor: 'pointer', fontFamily: 'inherit',
                        }}>Cancel</button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: T.muted }}>{step} / {STEPS.length}</span>
                        {step < STEPS.length ? (
                            <button
                                onClick={() => setStep(s => s + 1)}
                                disabled={!canProceed(step)}
                                style={{
                                    background: canProceed(step) ? T.mint : T.border2,
                                    border: 'none', borderRadius: 10, padding: '9px 20px',
                                    fontSize: 13, fontWeight: 700, color: '#fff',
                                    cursor: canProceed(step) ? 'pointer' : 'not-allowed',
                                    fontFamily: 'inherit',
                                    boxShadow: canProceed(step) ? '0 2px 10px rgba(15,122,74,0.25)' : 'none',
                                    transition: 'all 150ms',
                                }}
                            >Continue →</button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                style={{
                                    background: isSubmitting ? T.border2 : 'linear-gradient(135deg, #0f7a4a, #16a163)',
                                    border: 'none', borderRadius: 10, padding: '9px 22px',
                                    fontSize: 13, fontWeight: 700, color: '#fff',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    fontFamily: 'inherit',
                                    boxShadow: isSubmitting ? 'none' : '0 2px 14px rgba(15,122,74,0.30)',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    transition: 'all 150ms',
                                }}
                            >
                                {isSubmitting
                                    ? <><CircularProgress size={13} sx={{ color: '#fff' }} /> Saving…</>
                                    : isEditMode ? '✏️ Update Booking' : '💾 Save as Draft'
                                }
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <Snackbar
                open={snackbar.open} autoHideDuration={6000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default CreateNewBooking;
